// ─── API Client — substitui o Supabase client ─────────────────────────────────
// Aponta para a API PHP no HostGator.
// Configure VITE_API_URL no .env para apontar para o domínio real.
// Exemplo: VITE_API_URL=https://nutricionista-brenda.com.br/api

const API_BASE = (import.meta.env.VITE_API_URL as string) || '/api';

// ── Token helpers ─────────────────────────────────────────────────────────────
export const tokenStorage = {
  get: (): string | null => localStorage.getItem('auth_token'),
  set: (t: string): void  => localStorage.setItem('auth_token', t),
  clear: (): void          => localStorage.removeItem('auth_token'),
};

// ── Core fetch wrapper ────────────────────────────────────────────────────────
async function apiFetch<T = unknown>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const token = tokenStorage.get();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> | undefined),
  };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(`${API_BASE}${path}`, { ...options, headers });

  // 204 No Content
  if (res.status === 204) return undefined as T;

  const text = await res.text();
  let data: unknown;
  try { data = JSON.parse(text); } catch { data = { error: text }; }

  if (!res.ok) {
    const msg = (data as { error?: string })?.error ?? `HTTP ${res.status}`;
    throw new Error(msg);
  }

  return data as T;
}

// ─── Auth ─────────────────────────────────────────────────────────────────────
export const authApi = {
  login: (email: string, password: string) =>
    apiFetch<{ token: string; user: UserProfile }>('/auth/login', {
      method: 'POST', body: JSON.stringify({ email, password }),
    }),

  register: (email: string, password: string, name?: string) =>
    apiFetch<{ token: string; user: UserProfile }>('/auth/register', {
      method: 'POST', body: JSON.stringify({ email, password, name }),
    }),

  me: () => apiFetch<UserProfile>('/auth/me'),
};

// ─── Testimonials ─────────────────────────────────────────────────────────────
export const testimonialsApi = {
  list: (activeOnly = false) =>
    apiFetch<Testimonial[]>(`/testimonials?active_only=${activeOnly}`),

  create: (data: Omit<Testimonial, 'id' | 'created_at' | 'updated_at'>) =>
    apiFetch<Testimonial>('/testimonials', { method: 'POST', body: JSON.stringify(data) }),

  update: (id: string, data: Partial<Testimonial>) =>
    apiFetch<Testimonial>(`/testimonials/${id}`, { method: 'PUT', body: JSON.stringify(data) }),

  delete: (id: string) =>
    apiFetch<{ success: boolean }>(`/testimonials/${id}`, { method: 'DELETE' }),
};

// ─── FAQs ─────────────────────────────────────────────────────────────────────
export const faqsApi = {
  list: () => apiFetch<Faq[]>('/faqs'),

  create: (data: Omit<Faq, 'id' | 'created_at' | 'updated_at'>) =>
    apiFetch<Faq>('/faqs', { method: 'POST', body: JSON.stringify(data) }),

  update: (id: string, data: Partial<Faq>) =>
    apiFetch<Faq>(`/faqs/${id}`, { method: 'PUT', body: JSON.stringify(data) }),

  delete: (id: string) =>
    apiFetch<{ success: boolean }>(`/faqs/${id}`, { method: 'DELETE' }),
};

// ─── Blog Posts ───────────────────────────────────────────────────────────────
export const blogPostsApi = {
  list: (params?: { published?: boolean; category_id?: string; exclude_id?: string; limit?: number }) => {
    const q = new URLSearchParams();
    if (params?.published)    q.set('published', 'true');
    if (params?.category_id)  q.set('category_id', params.category_id);
    if (params?.exclude_id)   q.set('exclude_id', params.exclude_id);
    if (params?.limit)        q.set('limit', String(params.limit));
    return apiFetch<BlogPost[]>(`/blog/posts?${q}`);
  },

  featured: () =>
    apiFetch<BlogPost | null>('/blog/posts?published=true&featured=true'),

  get: (id: string) => apiFetch<BlogPost>(`/blog/posts/${id}`),

  create: (data: BlogPostInput) =>
    apiFetch<BlogPost>('/blog/posts', { method: 'POST', body: JSON.stringify(data) }),

  update: (id: string, data: Partial<BlogPostInput>) =>
    apiFetch<BlogPost>(`/blog/posts/${id}`, { method: 'PUT', body: JSON.stringify(data) }),

  delete: (id: string) =>
    apiFetch<{ success: boolean }>(`/blog/posts/${id}`, { method: 'DELETE' }),
};

// ─── Blog Categories ──────────────────────────────────────────────────────────
export const categoriesApi = {
  list: () => apiFetch<BlogCategory[]>('/blog/categories'),

  create: (data: Omit<BlogCategory, 'id' | 'created_at' | 'updated_at'>) =>
    apiFetch<BlogCategory>('/blog/categories', { method: 'POST', body: JSON.stringify(data) }),

  update: (id: string, data: Partial<BlogCategory>) =>
    apiFetch<BlogCategory>(`/blog/categories/${id}`, { method: 'PUT', body: JSON.stringify(data) }),

  delete: (id: string) =>
    apiFetch<{ success: boolean }>(`/blog/categories/${id}`, { method: 'DELETE' }),
};

// ─── Patient Journey ──────────────────────────────────────────────────────────
export const patientJourneyApi = {
  list: () => apiFetch<PatientJourneyStep[]>('/patient-journey'),

  create: (data: Omit<PatientJourneyStep, 'id'>) =>
    apiFetch<PatientJourneyStep>('/patient-journey', { method: 'POST', body: JSON.stringify(data) }),

  update: (id: string, data: Partial<PatientJourneyStep>) =>
    apiFetch<PatientJourneyStep>(`/patient-journey/${id}`, { method: 'PUT', body: JSON.stringify(data) }),

  delete: (id: string) =>
    apiFetch<{ success: boolean }>(`/patient-journey/${id}`, { method: 'DELETE' }),
};

// ─── Nossa Historia ───────────────────────────────────────────────────────────
export const nossaHistoriaApi = {
  content: () => apiFetch<NossaHistoriaContent[]>('/nossa-historia/content'),

  updateContent: (id: string, data: Partial<NossaHistoriaContent>) =>
    apiFetch<NossaHistoriaContent>(`/nossa-historia/content/${id}`, { method: 'PUT', body: JSON.stringify(data) }),

  timeline: () => apiFetch<TimelineEvent[]>('/nossa-historia/timeline'),

  createTimeline: (data: Omit<TimelineEvent, 'id'>) =>
    apiFetch<TimelineEvent>('/nossa-historia/timeline', { method: 'POST', body: JSON.stringify(data) }),

  updateTimeline: (id: string, data: Partial<TimelineEvent>) =>
    apiFetch<TimelineEvent>(`/nossa-historia/timeline/${id}`, { method: 'PUT', body: JSON.stringify(data) }),

  deleteTimeline: (id: string) =>
    apiFetch<{ success: boolean }>(`/nossa-historia/timeline/${id}`, { method: 'DELETE' }),

  team: () => apiFetch<TeamMember[]>('/nossa-historia/team'),

  createTeam: (data: Omit<TeamMember, 'id'>) =>
    apiFetch<TeamMember>('/nossa-historia/team', { method: 'POST', body: JSON.stringify(data) }),

  updateTeam: (id: string, data: Partial<TeamMember>) =>
    apiFetch<TeamMember>(`/nossa-historia/team/${id}`, { method: 'PUT', body: JSON.stringify(data) }),

  deleteTeam: (id: string) =>
    apiFetch<{ success: boolean }>(`/nossa-historia/team/${id}`, { method: 'DELETE' }),
};

// ─── Services ─────────────────────────────────────────────────────────────────
export const servicesApi = {
  list: (section?: string) => {
    const q = section ? `?section=${encodeURIComponent(section)}` : '';
    return apiFetch<ServiceContent[]>(`/services${q}`);
  },

  create: (data: Omit<ServiceContent, 'id'>) =>
    apiFetch<ServiceContent>('/services', { method: 'POST', body: JSON.stringify(data) }),

  update: (id: string, data: Partial<ServiceContent>) =>
    apiFetch<ServiceContent>(`/services/${id}`, { method: 'PUT', body: JSON.stringify(data) }),

  delete: (id: string) =>
    apiFetch<{ success: boolean }>(`/services/${id}`, { method: 'DELETE' }),
};

// ─── Site Settings ────────────────────────────────────────────────────────────
export const settingsApi = {
  list: () => apiFetch<SiteSetting[]>('/settings'),

  update: (key: string, value: string) =>
    apiFetch<SiteSetting>(`/settings/${key}`, { method: 'PUT', body: JSON.stringify({ value }) }),
};

// ─── Upload ───────────────────────────────────────────────────────────────────
export const uploadApi = {
  image: async (file: File): Promise<string> => {
    const token = tokenStorage.get();
    const form  = new FormData();
    form.append('file', file);

    const res = await fetch(`${API_BASE}/upload`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: form,
    });
    const data = await res.json() as { url?: string; error?: string };
    if (!res.ok) throw new Error(data.error ?? 'Erro no upload');
    return data.url!;
  },
};

// ─── Types ────────────────────────────────────────────────────────────────────

/** Tabela: users */
export interface UserProfile {
  id: string;
  email: string;
  name: string | null;
  role: 'admin' | 'patient';
  created_at: string | null;
  updated_at: string | null;
}

export interface Testimonial {
  id: string;
  author_name: string;
  testimonial_text: string;
  rating: number;
  author_role?: string | null;
  author_photo_url?: string | null;
  is_active: boolean;
  display_order: number;
  created_at: string;
  updated_at: string;
}

export interface Faq {
  id: string;
  question: string;
  answer: string;
  is_active: boolean;
  display_order: number;
  created_at: string;
  updated_at: string;
}

/** Tabela: blog_categories — inclui slug para URLs semânticas */
export interface BlogCategory {
  id: string;
  name: string;
  slug: string;
  color_hex: string;
  border_color_hex: string;
  text_color_hex: string;
  created_at: string;
  updated_at: string;
}

/** Tabela: blog_posts — inclui slug e read_time_minutes */
export interface BlogPost {
  id: string;
  slug: string | null;
  title: string;
  subtitle?: string | null;
  content?: string | null;
  content_formatted?: string | null;
  excerpt?: string | null;
  featured_image_url?: string | null;
  read_time_minutes?: number | null;
  category_id?: string | null;
  author_id?: string | null;
  published: boolean;
  is_featured: boolean;
  scheduled_for?: string | null;
  created_at: string;
  updated_at: string;
  blog_categories?: BlogCategory | null;
}

export interface BlogPostInput {
  title: string;
  slug?: string | null;
  subtitle?: string | null;
  content?: string | null;
  content_formatted?: string | null;
  excerpt?: string | null;
  featured_image_url?: string | null;
  read_time_minutes?: number | null;
  category_id?: string | null;
  published?: boolean;
  scheduled_for?: string | null;
}

export interface PatientJourneyStep {
  id: string;
  icon: string;
  title: string;
  description: string;
  is_active: boolean;
  display_order: number;
}

export interface TimelineEvent {
  id: string;
  year: string;
  title: string;
  description: string;
  is_active: boolean;
  display_order: number;
}

export interface TeamMember {
  id: string;
  name: string;
  position: string;
  description: string;
  photo_url: string;
  is_active: boolean;
  display_order: number;
}

/** Tabela: page_content (page = 'nossa_historia') */
export interface NossaHistoriaContent {
  id: string;
  page: 'nossa_historia';
  section: 'hero' | 'story' | 'principle';
  content_key: string;
  content_value: Record<string, unknown>;
  is_active: boolean;
  display_order: number;
}

/** Tabela: page_content (page = 'services') */
export interface ServiceContent {
  id: string;
  page: 'services';
  section: string;
  content_key: string;
  content_value: unknown;
  is_active: boolean | null;
  display_order: number | null;
}

export interface SiteSetting {
  key: string;
  value: string | null;
  description: string | null;
  is_public: boolean;
}
